import React, { useState, useEffect } from 'react';

export const API: React.FC = () => {
  const [apiHealth, setApiHealth] = useState<'loading' | 'healthy' | 'error'>('loading');

  useEffect(() => {
    checkApiHealth();
  }, []);

  const checkApiHealth = async () => {
    try {
      const response = await fetch('/api/health');
      if (response.ok) {
        setApiHealth('healthy');
      } else {
        setApiHealth('error');
      }
    } catch (error) {
      setApiHealth('error');
    }
  };

  const endpoints = [
    { method: 'GET', path: '/api/health', description: 'Health check endpoint' },
    { method: 'POST', path: '/api/login', description: 'User authentication' },
    { method: 'POST', path: '/api/register', description: 'User registration' },
    { method: 'GET', path: '/api/users', description: 'List all users' },
    { method: 'POST', path: '/api/users', description: 'Create new user' },
    { method: 'PUT', path: '/api/users/:id', description: 'Update user' },
    { method: 'DELETE', path: '/api/users/:id', description: 'Delete user' },
    { method: 'GET', path: '/api/projects', description: 'List projects' },
    { method: 'POST', path: '/api/projects', description: 'Create project' },
    { method: 'POST', path: '/files', description: 'Upload file to R2' },
    { method: 'GET', path: '/files', description: 'List user files' },
    { method: 'GET', path: '/files/:key', description: 'Download file' },
    { method: 'DELETE', path: '/files/:key', description: 'Delete file' }
  ];

  return (
    <div className="space-y-6">
      {/* API Status */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h2 className="text-2xl font-bold mb-4">🔌 API Status</h2>
        <div className="flex items-center space-x-4">
          <div className={`flex items-center space-x-2 px-4 py-2 rounded-full ${
            apiHealth === 'healthy' ? 'bg-green-100 text-green-800' :
            apiHealth === 'error' ? 'bg-red-100 text-red-800' :
            'bg-yellow-100 text-yellow-800'
          }`}>
            <div className={`w-3 h-3 rounded-full ${
              apiHealth === 'healthy' ? 'bg-green-500 animate-pulse' :
              apiHealth === 'error' ? 'bg-red-500' :
              'bg-yellow-500 animate-pulse'
            }`} />
            <span className="font-medium">
              {apiHealth === 'healthy' ? 'All Systems Operational' :
               apiHealth === 'error' ? 'Connection Error' :
               'Checking Status...'}
            </span>
          </div>
          <button
            onClick={checkApiHealth}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Refresh Status
          </button>
        </div>
      </div>

      {/* Endpoints */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-xl font-semibold mb-4">📚 Available Endpoints</h3>
        <div className="space-y-2">
          {endpoints.map((endpoint, idx) => (
            <div
              key={idx}
              className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center space-x-4">
                <span className={`px-3 py-1 rounded text-xs font-bold text-white ${
                  endpoint.method === 'GET' ? 'bg-green-500' :
                  endpoint.method === 'POST' ? 'bg-blue-500' :
                  endpoint.method === 'PUT' ? 'bg-yellow-500' :
                  'bg-red-500'
                }`}>
                  {endpoint.method}
                </span>
                <code className="font-mono text-sm">{endpoint.path}</code>
              </div>
              <span className="text-sm text-gray-600">{endpoint.description}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Configuration */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-xl font-semibold mb-4">⚙️ Configuration</h3>
        <div className="space-y-3">
          <div className="flex justify-between py-2">
            <span className="text-gray-600">API Worker URL</span>
            <code className="text-sm bg-gray-100 px-3 py-1 rounded">https://goalyet-api.b-9f2.workers.dev</code>
          </div>
          <div className="flex justify-between py-2">
            <span className="text-gray-600">Files Worker URL</span>
            <code className="text-sm bg-gray-100 px-3 py-1 rounded">https://goalyet-files.b-9f2.workers.dev</code>
          </div>
          <div className="flex justify-between py-2">
            <span className="text-gray-600">Environment</span>
            <code className="text-sm bg-gray-100 px-3 py-1 rounded">Production</code>
          </div>
          <div className="flex justify-between py-2">
            <span className="text-gray-600">Database</span>
            <code className="text-sm bg-gray-100 px-3 py-1 rounded">Cloudflare D1</code>
          </div>
          <div className="flex justify-between py-2">
            <span className="text-gray-600">Storage</span>
            <code className="text-sm bg-gray-100 px-3 py-1 rounded">Cloudflare R2</code>
          </div>
        </div>
      </div>

      {/* Test Connection */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4">🧪 Test Connection</h3>
        <p className="text-gray-600 mb-4">
          Click the button below to test the API connection and verify all services are operational.
        </p>
        <button
          onClick={async () => {
            try {
              const response = await fetch('/api/health');
              const data = await response.json();
              alert(`API Response: ${JSON.stringify(data, null, 2)}`);
            } catch (error) {
              alert(`Error: ${error}`);
            }
          }}
          className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors font-medium"
        >
          🚀 Test API Connection
        </button>
      </div>
    </div>
  );
};